import React, { useState, useEffect } from 'react';
import Pusher from 'pusher-js';
import axios from 'axios';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import {
    WIDGET_MESSAGE, USER_DETAILS, GET_MESSAGES,
    SEND_OTP, VERIFY_OTP, CREATE_ACCOUNT, VALIDATE_USER
} from './Apis';
import { encStorage } from "./encStorage";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHand } from '@fortawesome/free-solid-svg-icons';

const ChatWidget = ({ config, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [channelName] = useState(config.name);
    const [isChatStarted, setIsChatStarted] = useState(encStorage.getItem('isChatStarted') ?? false);
    const [userId, setUserId] = useState(encStorage.getItem('id') ?? null);
    const [step, setStep] = useState(0);
    const [initialStep, setInitialStep] = useState(null);
    const [userDetails, setUserDetails] = useState({ name: '', email: '', phone_no: '', accountId: '' });
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    //alert(screenWidth)
    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const botSay = (text, delay = 0) => {
        setTimeout(() => {
            setMessages(prev => [...prev, { text, isMine: false }]);
        }, delay);
    };

    useEffect(() => {
        let inactivityTimeout;

        const resetInactivityTimer = () => {
            clearTimeout(inactivityTimeout);
            inactivityTimeout = setTimeout(() => {
                console.log('User inactive for 15 minutes. Clearing storage.');
                encStorage.clear();
            }, 15 * 60 * 1000); // 15 minutes
        };

        const clearOnClose = () => {
            console.log('Tab closed. Clearing storage.');
            encStorage.clear();
        };

        // Set/reset timer on user interaction
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(event => window.addEventListener(event, resetInactivityTimer));

        // Start timer initially
        resetInactivityTimer();

        // Clear storage on tab close
        window.addEventListener('beforeunload', clearOnClose);

        return () => {
            clearTimeout(inactivityTimeout);
            events.forEach(event => window.removeEventListener(event, resetInactivityTimer));
            window.removeEventListener('beforeunload', clearOnClose);
        };
    }, []);

    useEffect(() => {
        if (!isChatStarted && !initialStep) {
            botSay(
                <>
                    👋
                    {" Welcome to " + config.companyName + "."}
                </>
            );

            botSay("How can i assist you today?");
            botSay(
                <>
                    <button
                        style={{
                            fontSize: screenWidth <= 1000 ? '40px' : '16px',
                            border: '1px solid ' + config?.headerBackgroundColor,
                            backgroundColor: '#fff',
                            color: config?.headerBackgroundColor,
                            borderRadius: '10px',
                            padding: screenWidth <= 1000 ? '10px' : '5px 10px',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleBotFlow("~Query")}
                    >
                        Have Queries?
                    </button>
                    <button
                        style={{
                            fontSize: screenWidth <= 1000 ? '40px' : '16px',
                            border: '1px solid ' + config?.headerBackgroundColor,
                            backgroundColor: '#fff',
                            color: config?.headerBackgroundColor,
                            borderRadius: '10px',
                            padding: screenWidth <= 1000 ? '10px' : '5px 10px',
                            marginTop: '10px',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleBotFlow("~Register")}
                    >
                        Register New Account
                    </button>
                    <button
                        style={{
                            fontSize: screenWidth <= 1000 ? '40px' : '16px',
                            border: '1px solid ' + config?.headerBackgroundColor,
                            backgroundColor: '#fff',
                            color: config?.headerBackgroundColor,
                            borderRadius: '10px',
                            padding: screenWidth <= 1000 ? '10px' : '5px 10px',
                            marginTop: '10px',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleBotFlow("~Existing")}
                    >
                        Existing User
                    </button>
                </>
            );
        }
    }, [initialStep, isChatStarted]);

    useEffect(() => {
        if (isChatStarted && userId) {
            const pusher = new Pusher('ee57363e088976739ff2', { cluster: 'eu' });
            const eventName = `${channelName}_${userId}`;
            const channel = pusher.subscribe(channelName);

            channel.bind(eventName, function (data) {
                const message = {
                    text: data.message.message,
                    isMine: false,
                    created_at: new Date().toISOString(),
                    ...data.message
                };
                setMessages(prevMessages => [...prevMessages, message]);
            });

            return () => {
                channel.unbind_all();
                channel.unsubscribe();
            };
        }
    }, [isChatStarted, userId, channelName]);

    useEffect(() => {
        if (isChatStarted) {
            axios.get(GET_MESSAGES, { params: { id: userId } })
                .then(res => {
                    const loadedMessages = res.data.map(msg => ({
                        text: msg.message,
                        isMine: msg.sent_by === 'user',
                        created_at: msg.created_at
                    }));
                    //setMessages(loadedMessages);
                    setMessages(prev => [...prev, ...loadedMessages]);
                })
                .catch(console.error);
        }
    }, [isChatStarted, userId]);

    const sendUserMessage = (text) => {
        setMessages(prev => [...prev, { text, isMine: true }]);
    };

    const handleBotFlow = async (text) => {
        sendUserMessage(text);
        if (!initialStep) {
            const choice = text.toLowerCase();
            if (choice.includes("~query")) {
                setInitialStep('~query');
                botSay("Great! What's your name?", 2000);
                setStep(0);
                return;
            } else if (choice.includes("~register")) {
                setInitialStep('~register');
                botSay("Let's get started.", 2000);
                botSay("What's your name?", 4000);
                setStep(10);
                return;
            } else if (choice.includes("~existing")) {
                setInitialStep('~existing');
                botSay("Let's get started.", 2000);
                botSay("What's your Account Id/Username?", 4000);
                setStep(15);
                return;
            } else {
                botSay("Please click one of the option.", 2000);
                return;
            }
        }

        if (initialStep === '~query') {
            //console.log('step', step)
            if (step === 0) {
                if (!text.trim() || text.length < 2) {
                    botSay("Enter a valid name (at least 2 characters).", 2000);
                    return;
                }
                setUserDetails(prev => ({ ...prev, name: text }));
                encStorage.setItem('name', text);
                botSay(`Hi ${text}, what's your email?`, 2000);
                setStep(1);
            } else if (step === 1) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                //console.log(`Raw text: "${text}", Trimmed: "${text.trim()}", IsValid: ${emailRegex.test(text.trim())}`);
                if (!emailRegex.test(text.trim())) {
                    botSay("Hmm... that doesn't look like a valid email. Try again?", 2000);
                    return;
                }
                setUserDetails(prev => ({ ...prev, email: text }));
                encStorage.setItem('email', text);
                botSay("Now your phone number?");
                setStep(2);
            } else if (step === 2) {
                const phoneRegex = /^[0-9]{10,15}$/;
                if (!phoneRegex.test(text.trim())) {
                    botSay("Invalid phone number. Try again.", 2000);
                    return;
                }

                const fullDetails = {
                    ...userDetails,
                    phone_no: text,
                    brand_id: config?.brand
                };

                setUserDetails(prev => ({ ...prev, phone_no: text }));
                encStorage.setItem('phone_no', text);
                botSay("Thanks! Starting your chat...");

                try {
                    const res = await axios.post(USER_DETAILS, fullDetails);
                    encStorage.setItem('id', res.data.user.id);
                    encStorage.setItem('isChatStarted', true);
                    setUserId(res.data.user.id);
                    setIsChatStarted(true);
                    const newMessage = {
                        text: "You're connected. Post your query!",
                        created_at: new Date().toISOString(),
                        isMine: false // because it's a bot/system message, not user's own message
                    };

                    setMessages(prev => [...prev, newMessage]);
                    //sendMessage(newMessage);
                } catch {
                    botSay("Something went wrong. Please try again.");
                }
            }
        }

        // New Account flow
        if (initialStep === '~register') {
            if (step === 10) {
                setUserDetails(prev => ({ ...prev, name: text }));
                botSay("Thanks " + text.trim(), 2000);
                botSay("Please enter your email?", 4000);
                setStep(11);
            } else if (step === 11) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(text.trim())) {
                    botSay("Invalid email. Please Try again.", 2000);
                    return;
                }
                setUserDetails(prev => ({ ...prev, email: text.trim() }));
                botSay("Great!! Please share your phone number?", 2000);
                setStep(12);
            } else if (step === 12) {
                const phoneRegex = /^(?:\+?(91|1))?[6-9]\d{9}$/;
                if (!phoneRegex.test(text.trim())) {
                    botSay("Invalid phone number. Try again.");
                    return;
                }
                const fullNumber = text.trim().replace(/\s+/g, ''); // remove spaces
                const last10Digits = fullNumber.slice(-10); // get last 10 digits
                const countryCode = fullNumber.replace(last10Digits, ''); // get country code

                const updatedUserDetails = {
                    ...userDetails,
                    phone_no: last10Digits,
                    country_code: countryCode || '+91' // fallback if user entered just 10 digits
                };
                setUserDetails(updatedUserDetails);
                botSay("Sending OTP...", 1000);
                try {
                    await axios.post(SEND_OTP + '/' + config.brand, { phone: text.trim() });
                    botSay("Enter the OTP sent to your phone.", 3000);
                    setStep(13);
                } catch {
                    botSay("OTP sending failed. Try again.");
                }
            } else if (step === 13) {
                const otpRegex = /^\d{6}$/;
                if (!otpRegex.test(text.trim())) {
                    botSay("Invalid OTP. Try again.");
                    return;
                }
                botSay("Verifing OTP...");
                const currentDetails = { ...userDetails };
                try {

                    //console.log("Current details:", currentDetails);
                    await axios.post(VERIFY_OTP + '/' + config.brand, { phone: currentDetails.country_code + '' + currentDetails.phone_no, otp: text.trim() });
                    botSay("Your Phone no is verfied ,Please wait while i am creating your account");
                    setStep(14);

                    const password = Math.floor(100000 + Math.random() * 900000).toString();
                    const res = await axios.post(CREATE_ACCOUNT + '/' + config.brand, {
                        name: currentDetails.name,
                        email: currentDetails.email,
                        phone_no: currentDetails.phone_no,
                        country_code: currentDetails.country_code,
                        brand_id: config?.brand,
                        password: password
                    });
                    //encStorage.setItem('id', res.data.user.id);
                    encStorage.setItem('isChatStarted', false);
                    //setUserId(res.data.user.id);

                    const { client, template } = res.data;

                    // Save session details
                    //encStorage.setItem('id', res.data.user.id);
                    encStorage.setItem('isChatStarted', false);
                    // setUserId(res.data.user.id);
                    // setIsChatStarted(false);

                    // // Replace template placeholders with actual values
                    let personalizedMessage = template
                        .replace(/\\n/g, "<br>")
                        .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" style="color:#007bff;text-decoration:underline;">$1</a>');


                    // Post message to chat
                    botSay("Congratulations !! Your account is created.", 2000);
                    botSay(personalizedMessage, 3000);

                } catch (error) {
                    //console.error(error);

                    const errors = error.response?.data?.errors;

                    if (Array.isArray(errors) && errors.length > 0) {
                        errors.forEach(err => botSay(err));
                    } else {
                        botSay("Something went wrong. Please try again.");
                    }
                }
            }
        }

        if (initialStep === '~existing') {
            if (step === 15) {
                setUserDetails(prev => ({ ...prev, accountId: text.trim() }));
                botSay("Thanks we are validating your account " + text.trim());

                try {
                    const clientDetails = {
                        account_id: text.trim(),
                        brand_id: config?.brand
                    };
                    const res = await axios.post(VALIDATE_USER, clientDetails);
                    if (res.data.status === 'error') {
                        botSay(res.data.message);
                        return;
                    }
                    encStorage.setItem('id', res.data.user.id);
                    encStorage.setItem('isChatStarted', true);
                    setUserId(res.data.user.id);
                    botSay("Hi " + res.data.user.name + ", \n Please select your query type", 1000);
                    botSay(
                        <>
                            <button
                                style={{
                                    fontSize: screenWidth <= 1000 ? '40px' : '16px',
                                    border: '1px solid ' + config?.headerBackgroundColor,
                                    backgroundColor: '#fff',
                                    color: config?.headerBackgroundColor,
                                    borderRadius: '10px',
                                    padding: screenWidth <= 1000 ? '10px' : '5px 10px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => sendMessage("Deposit")}
                            >
                                Deposit
                            </button>
                            <button
                                style={{
                                    fontSize: screenWidth <= 1000 ? '40px' : '16px',
                                    border: '1px solid ' + config?.headerBackgroundColor,
                                    backgroundColor: '#fff',
                                    color: config?.headerBackgroundColor,
                                    borderRadius: '10px',
                                    marginTop: '10px',
                                    marginLeft: '10px',
                                    padding: screenWidth <= 1000 ? '10px' : '5px 10px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => sendMessage("Withdraw")}
                            >
                                Withdrawal
                            </button>
                            <button
                                style={{
                                    fontSize: screenWidth <= 1000 ? '40px' : '16px',
                                    border: '1px solid ' + config?.headerBackgroundColor,
                                    backgroundColor: '#fff',
                                    color: config?.headerBackgroundColor,
                                    borderRadius: '10px',
                                    marginTop: '10px',
                                    padding: screenWidth <= 1000 ? '10px' : '5px 10px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => sendMessage("General Support")}
                            >
                                General Support
                            </button>
                        </>
                        , 1500);
                    setStep(16);
                    setIsChatStarted(true);
                } catch (error) {
                    const errorMessage = error?.response?.data?.message || "Something went wrong. Please try again.";
                    botSay(errorMessage);
                }
            } else if (step === 16) {
                if (text.trim() !== "Deposit" && text.trim() !== "Withdraw" && text.trim() !== "Support") {
                    botSay("Please select a valid option.");
                    return;
                }
                setUserDetails(prev => ({ ...prev, query: text.trim() }));
                //encStorage.setItem('query', text.trim());
                botSay("Please post your query.");
                setStep(17);
            } else if (step === 17) {
                botSay("Thanks for your query. Please wait while we connect you to an agent.");
                // setStep(17);
            }
        }
    };

    const sendMessage = async (text) => {
        const newMessage = { text, created_at: new Date().toISOString(), isMine: true };
        setMessages(prev => [...prev, newMessage]);
        const user_id = encStorage.getItem('id');
        try {
            await axios.post(WIDGET_MESSAGE, {
                message: text,
                channel: channelName,
                id: user_id,
                brand_id: config?.brand
            });
            if (text.trim() === "Deposit" || text.trim() === "Withdraw" || text.trim() === "General Support") {
                const newBotMessage = {
                    text: "Please post your query related to " + text,
                    created_at: new Date().toISOString(),
                    isMine: false // because it's a bot/system message, not user's own message
                };
                setMessages(prev => [...prev, newBotMessage]);
            }
        } catch (error) {
            console.error('Message sending failed:', error);
        }
    };

    // Position styles
    const positionStyles = {
        position: 'fixed',
        ...(config?.position === 'bottom-right' && { bottom: '20px', right: '20px' }),
        ...(config?.position === 'bottom-left' && { bottom: '20px', left: '20px' }),
        ...(config?.position === 'top-right' && { top: '20px', right: '20px' }),
        ...(config?.position === 'top-left' && { top: '20px', left: '20px' }),
    };

    // Main widget styles
    let widgetStyles = {
        ...positionStyles,
        fontFamily: 'sans-serif',
        fontSize: '18px',
        width: config?.width || '300px',
        height: config?.height || '400px',
        border: config?.border || '1px solid #ccc',
        borderRadius: '20px',
        backgroundColor: config?.backgroundColor || '#fff',
        color: config?.textColor || '#000',
        boxShadow: config?.boxShadow || '0 2px 10px rgba(0,0,0,0.1)',
        zIndex: config?.zIndex || 9999,
        display: 'flex',
        flexDirection: 'column',
    };

    if (screenWidth <= 1000) {
        widgetStyles = {
            ...widgetStyles,
            width: '100%',
            height: '100%',
            fontSize: screenWidth <= 1000 ? '45px' : '16px',
            borderRadius: '20px',
            boxShadow: 'none',
            bottom: 0,
            right: 0,
        };
    }

    const headerStyles = {
        height: '10%',
        padding: '0 10px',
        backgroundColor: config?.headerBackgroundColor || '#007bff',
        color: config?.headerTextColor || '#fff',
        display: 'flex',
        borderRadius: '18px 18px 0 0',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: screenWidth <= 1000 ? '40px' : '18px',
    };
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    // const renderInitialOptions = () => (
    //     <div style={{ height: '5%', padding: 10, display: 'flex', gap: 20, justifyContent: 'center' }}>
    //         <button style={{ fontSize: screenWidth <= 1000 ? '40px' : '16px', border: '1px solid ' + config?.headerBackgroundColor, backgroundColor: '#fff', color: config?.headerBackgroundColor || '#000', borderRadius: '10px', padding: screenWidth <= 1000 && isSafari ? '1px 10px' : '5px 10px' }} onClick={() => handleBotFlow("Query")}>Have Queries?</button>
    //         <button style={{ fontSize: screenWidth <= 1000 ? '40px' : '16px', border: '1px solid ' + config?.headerBackgroundColor, backgroundColor: '#fff', color: config?.headerBackgroundColor, borderRadius: '10px', padding: screenWidth <= 1000 && isSafari ? '1px 10px' : '5px 10px' }} onClick={() => handleBotFlow("Register")}>Register New Account</button>
    //     </div>
    // );

    return (
        <div className="chat-widget" style={widgetStyles}>
            <div style={headerStyles}>
                <span style={{ display: "flex", alignItems: 'center' }}><img src="https://zedlytics.s3.me-central-1.amazonaws.com/sara.png" style={{ width: screenWidth <= 1000 ? '80px' : '35px', marginRight: '10px' }} />{config?.title || 'Chat'}</span>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: screenWidth <= 1000 ? '80px' : '40px', right: screenWidth <= 1000 ? '10px' : '30px' }}>×</button>
            </div>
            <MessageList messages={messages} />
            <MessageInput onSend={isChatStarted ? sendMessage : handleBotFlow} config={config} />
        </div>
    );
};

export default ChatWidget;
